import { AbstractApiService } from '@api/service';
import { IParticipantGroup } from '@legacy/types';
import jsonToFormData from 'json-form-data';
import { createFilterParamsString } from '../../../../../utils';
import { ParticipantGroupServiceIndexQueryDTO } from './ParticipantGroupServiceIndexQueryDTO';
import { ParticipantGroupServiceImportCSVResponse } from './ParticipantGroupServiceImportCSVResponse';

export class ParticipantGroupService extends AbstractApiService {
  /**
   * Fetches a list of participant groups based on the provided query parameters.
   */
  async index(query: ParticipantGroupServiceIndexQueryDTO) {
    const request = await this.axios.get<{ data: IParticipantGroup[] }>(
      createFilterParamsString(query, '/participant-groups')
    );
    return request.data.data;
  }

  /**
   * Associates an offline submission PDF with the specified participant group and survey stage.
   */
  async associateOfflineSubmissionPdf(
    participantGroupId: number,
    surveyStageId: number,
    file: File
  ) {
    const formData = jsonToFormData({ survey_stage_id: surveyStageId, pdf: file });

    const request = await this.axios.post<{ data: { url: string } }>(
      `/participant-groups/${participantGroupId}/associate-offline-submission-pdf?_method=PUT`,
      formData
    );
    return request.data.data;
  }

  /**
   * Disassociates the offline submission PDF from a participant group for a specific survey stage.
   */
  async disassociateOfflineSubmissionPdf(
    participantGroupId: number,
    surveyStageId: number
  ) {
    return await this.axios.put(
      `/participant-groups/${participantGroupId}/disassociate-offline-submission-pdf`,
      {
        survey_stage_id: surveyStageId,
      }
    );
  }

  /**
   * Imports participant data from a CSV file for a specific survey stage.
   */
  async importCsv(
    surveyStageId: number,
    file: File
  ): Promise<ParticipantGroupServiceImportCSVResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('survey_stage_id', surveyStageId.toString());

    const response = await this.axios.post<ParticipantGroupServiceImportCSVResponse>(
      '/participants/check-csv',
      formData
    );

    return response.data;
  }

  async createGroupForAdmin(surveyId: number) {
    return await this.axios.post(
      '/participants/get-or-create-participant-group-for-admin',
      {
        survey_id: surveyId,
      }
    );
  }
}
